/**
 * OIH Heritage — Progress Plan form schemas.
 *
 * 7-step wizard:
 *   1. PROFILE_SCHEMA         → "Your Information"
 *   2. ABOUT_ITEM_SCHEMA      → "About the Heritage Item"
 *   3. DATES_SCHEMA           → "Important Dates & History"  (conditional on item type)
 *   4. HISTORICAL_SCHEMA      → "Provenance & Historical Significance"
 *   5. (uploads — not a schema)
 *   6. PERMISSION_SCHEMA      → "Privacy, Consent & Agreement"
 *   7. (review — no fields)
 *
 * Adding, reordering, renaming, or making a field required does NOT touch
 * workflow logic. Only the wizard's step-array cares about which schema goes
 * where. Adding a NEW step means: (a) add a new schema; (b) insert its id in
 * `WIZARD_STEPS` in SubmissionWizard.jsx.
 */

// ------------------------------------------------------------------
//  HERITAGE ITEM CATEGORIES  (OIH Progress Plan)
// ------------------------------------------------------------------
export const HERITAGE_CATEGORIES = [
  "Audio",
  "Video",
  "Objects",
  "Letters",
  "Documents",
  "Old or Rare Books",
  "Manuscripts",
  "Photographs / Images",
  "Art / Artwork",
  "Textiles / Clothing",
  "Memorabilia",
  "Coins",
  "Other Heritage Materials",
];

export const CATEGORY_ICONS = {
  "Audio":                     "Mic",
  "Video":                     "Video",
  "Objects":                   "Package",
  "Letters":                   "Mail",
  "Documents":                 "FileText",
  "Old or Rare Books":         "BookOpen",
  "Manuscripts":               "ScrollText",
  "Photographs / Images":      "Image",
  "Art / Artwork":             "Palette",
  "Textiles / Clothing":       "Shirt",
  "Memorabilia":               "Award",
  "Coins":                     "Coins",
  "Other Heritage Materials":  "Sparkles",
};

// ------------------------------------------------------------------
//  1) PROFILE  →  Step 1 "Your Information"
// ------------------------------------------------------------------
export const PROFILE_SCHEMA = {
  sections: [
    {
      id: "identity",
      title: "About You",
      description: "This information helps the Heritage team contact you about your submission.",
      fields: [
        { key: "first_name", label: "First Name", type: "text", required: true, span: 6 },
        { key: "last_name",  label: "Last Name",  type: "text", required: true, span: 6 },
        { key: "email",      label: "Email",      type: "email", required: true, span: 6 },
        { key: "phone",      label: "Phone",      type: "tel",   span: 6 },
      ],
    },
    {
      id: "location",
      title: "Where You're Based",
      fields: [
        { key: "city",       label: "City", type: "text", span: 6 },
        { key: "province",   label: "Province / State", type: "text", span: 6 },
        { key: "country",    label: "Country", type: "text", span: 6 },
        { key: "jamatkhana", label: "Jamatkhana / Community", type: "text", span: 6 },
      ],
    },
    {
      id: "contact",
      title: "Contact Preferences",
      fields: [
        { key: "preferred_contact", label: "Preferred Contact Method", type: "select",
          options: ["Email", "Phone", "Text Message", "No Preference"], span: 12 },
      ],
    },
  ],
};

// ------------------------------------------------------------------
//  2) ABOUT THE ITEM  →  Step 2
// ------------------------------------------------------------------
export const ABOUT_ITEM_SCHEMA = {
  sections: [
    {
      id: "item",
      title: "About the Heritage Item",
      description: "Tell us about the artifact, story, photograph, document, or object you would like to share.",
      fields: [
        { key: "title", label: "Item Name / Title", type: "text", required: true, span: 12,
          placeholder: "e.g., Grandmother's Wedding Odhni, 1955" },
        { key: "type", label: "Type of Heritage Item", type: "select", required: true, span: 6,
          options: HERITAGE_CATEGORIES },
        { key: "type_other", label: "Please specify item type", type: "text", span: 6,
          showIf: (v) => v.type === "Other Heritage Materials",
          required: true,
          placeholder: "Describe the type of heritage material" },
        { key: "short_description", label: "Short Description", type: "text", required: true, span: 12,
          placeholder: "One-sentence summary" },
        { key: "detailed_description", label: "Detailed Description", type: "textarea", span: 12, rows: 5,
          placeholder: "Please describe the item's appearance, material, dimensions, and any notable features." },
        { key: "location", label: "Location Associated With Item", type: "text", span: 6,
          placeholder: "City, Country" },
        { key: "community", label: "Community / Jamat Associated", type: "text", span: 6 },
      ],
    },
  ],
};

// ------------------------------------------------------------------
//  3) DATES  →  Step 3 "Important Dates & History"  (conditional)
// ------------------------------------------------------------------
export const DATES_SCHEMA = {
  sections: [
    {
      id: "dates",
      title: "Important Dates",
      description: "Provide dates you know — approximate values are acceptable.",
      fields: [
        { key: "date_of_recording", label: "Date of Recording", type: "text", span: 6,
          showIf: (v) => v.type === "Audio" || v.type === "Video",
          placeholder: "e.g., April 12, 1978" },
        { key: "date_of_origin", label: "Date of Origin", type: "text", span: 6,
          showIf: (v) => v.type === "Objects",
          placeholder: "e.g., Circa 1920" },
        { key: "approximate_date", label: "Approximate Date", type: "text", span: 6,
          showIf: (v) => !["Audio", "Video", "Objects"].includes(v.type),
          placeholder: "e.g., 1950s, or Circa 1920",
          help: "The approximate date the item was created, written, or made." },
        { key: "date_of_acquisition", label: "Date of Acquisition", type: "text", span: 6,
          placeholder: "When did the item come into your family?",
          help: "Approximate is fine." },
        { key: "related_people", label: "Related people", type: "text", span: 6 },
        { key: "related_places", label: "Related places or events", type: "text", span: 6 },
      ],
    },
  ],
};

// ------------------------------------------------------------------
//  4) PROVENANCE & HISTORY  →  Step 4
// ------------------------------------------------------------------
export const HISTORICAL_SCHEMA = {
  sections: [
    {
      id: "historical",
      title: "Provenance & Historical Significance",
      description: "Share the story and history behind this item. Even partial details help.",
      fields: [
        { key: "importance", label: "Why is this item important?", type: "textarea", rows: 3, span: 12 },
        { key: "story", label: "What story does it tell?", type: "textarea", rows: 3, span: 12 },
        { key: "creator", label: "Who created it?", type: "text", span: 6 },
        { key: "is_owner", label: "Are you the owner of this Heritage Item?", type: "select",
          options: ["Yes", "No"], span: 6, required: true },
        { key: "other_owner", label: "Who is the owner of this Heritage Item?", type: "text", span: 12,
          showIf: (v) => v.is_owner === "No", required: true,
          placeholder: "Name and relationship to you" },
        { key: "owner_history", label: "Who previously owned or used it?", type: "text", span: 12 },
        { key: "acquired", label: "How did it come into your possession?", type: "textarea", rows: 2, span: 12 },
        { key: "additional", label: "Additional historical information", type: "textarea", rows: 3, span: 12 },
      ],
    },
  ],
};

// ------------------------------------------------------------------
//  5) UPLOAD helper text (no schema — wizard renders custom UI)
// ------------------------------------------------------------------
export const UPLOAD_HELP = {
  title: "Upload Files",
  description:
    "Upload photographs, scans, audio, video, or documents related to this Heritage Item. " +
    "Supported: JPG, PNG, WEBP, PDF, DOC/DOCX, TXT — up to 20 MB per file. You may add up to 10 files.",
  cannot_upload_label: "I am unable to upload media files and would like assistance from the Heritage team",
};

// ------------------------------------------------------------------
//  6) CONSENT  →  Step 6
// ------------------------------------------------------------------
export const CONSENT_TEXT = [
  "I confirm that the information I have provided about this Heritage Item is accurate to the best of my knowledge.",
  "I understand that submitting information to the Office of Ismaili Heritage does not guarantee review, digitization, preservation, or inclusion in the final Heritage Collection.",
  "I permit the Heritage team to contact me if additional information is required about this submission.",
  "Where I have indicated that I am the owner or have permission to share this Heritage Item, I grant the Office of Ismaili Heritage a non-exclusive licence to preserve, document, and — where approved — publicly display this item for community heritage purposes.",
  "I understand that my personal information will be handled in accordance with OIH privacy practices and will not be published publicly without my explicit consent.",
];

export const PERMISSION_SCHEMA = {
  sections: [
    {
      id: "permissions",
      title: "Privacy, Consent & Agreement",
      description: "Please read the following before submitting.",
      fields: [
        { key: "privacy_concerns", label: "Are there any privacy concerns or restrictions we should know about?", type: "textarea", rows: 2, span: 12,
          placeholder: "e.g., Please do not publish names of living family members." },
        { key: "own_or_permission", label: "I own this item OR I have permission from the owner to share it", type: "checkbox", required: true, span: 12 },
        { key: "permit_contact", label: "I permit the Heritage team to contact me about this submission", type: "checkbox", required: true, span: 12 },
        { key: "understand_no_guarantee", label: "I understand that submission does not guarantee review, preservation, or publication", type: "checkbox", required: true, span: 12 },
        { key: "consent_agreement", label: "I have read and agree to the Consent & Agreement above", type: "checkbox", required: true, span: 12 },
        { key: "electronic_signature", label: "Electronic Signature (type your full name)", type: "text", required: true, span: 12,
          placeholder: "Full legal name" },
      ],
    },
  ],
};

// ------------------------------------------------------------------
//  HERITAGE ITEM CARE  (shown after submission — not part of wizard)
// ------------------------------------------------------------------
export const HERITAGE_CARE_TIPS = [
  { title: "Document Important Details", body: "Note down the item's origin, dates, people, and any stories attached to it. Written context is often as valuable as the item itself." },
  { title: "Store Items Carefully", body: "Use acid-free tissue and archival boxes where possible. Avoid attics, basements, and areas with temperature swings." },
  { title: "Protect From Direct Sunlight", body: "UV light fades photographs, textiles, and inks. Keep items away from windows and strong artificial light." },
  { title: "Handle With Clean Hands", body: "Wash and dry hands before handling. Wear cotton gloves for photographs and delicate paper documents." },
  { title: "Clean Gently", body: "Dust only with soft brushes. Never use household cleaners on antique surfaces without professional advice." },
  { title: "Protect Against Pests", body: "Silverfish, moths, and rodents can destroy paper and textiles. Inspect storage areas regularly." },
  { title: "Regularly Inspect for Damage", body: "Check items once a year for mould, tearing, insect activity, or fading. Early attention prevents further loss." },
  { title: "Manage Humidity", body: "Aim for 40–55% relative humidity. Excessive dryness cracks materials; excessive moisture encourages mould." },
  { title: "Seek Professional Advice", body: "For rare, fragile, or damaged items, consult a professional conservator before attempting repair." },
];

// ------------------------------------------------------------------
//  Helpers
// ------------------------------------------------------------------
export function getInitialValues(schema) {
  const out = {};
  schema.sections.forEach(s => s.fields.forEach(f => {
    out[f.key] = f.type === "checkbox" ? false : "";
  }));
  return out;
}

export function validateSchema(schema, values) {
  const errors = {};
  schema.sections.forEach(s => s.fields.forEach(f => {
    if (f.showIf && !f.showIf(values)) return;
    if (!f.required) return;
    const v = values[f.key];
    if (f.type === "checkbox") {
      if (!v) errors[f.key] = "This confirmation is required";
    } else if (!v || String(v).trim() === "") {
      errors[f.key] = "This field is required";
    }
  }));
  return errors;
}
